import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/Shared";
import { Logo } from "@/components/Logo";
import { LegalFooter } from "@/components/LegalFooter";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1601467995997-ac1ae9a8fff4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export const BrandPanel = () => (
  <div className="hidden lg:flex relative flex-col justify-between p-12 text-white overflow-hidden bg-emerald-950">
    <img src={HERO} alt="Work truck on the road" className="absolute inset-0 w-full h-full object-cover opacity-30" />
    <div className="absolute inset-0 bg-gradient-to-t from-emerald-950 via-emerald-950/70 to-emerald-950/30" />
    <div className="relative">
      <Logo light />
    </div>
    <div className="relative max-w-md">
      <p className="mt-overline !text-emerald-300 mb-4">For people who drive for work</p>
      <h2 className="font-heading text-4xl xl:text-5xl font-extrabold leading-[1.05] tracking-tight">Every business mile is money back.</h2>
      <ul className="mt-8 space-y-3 text-emerald-50/90">
        {["Log trips in seconds from your phone", "Keep receipts and expenses in one place", "See your estimated deduction all year"].map((t) => (
          <li key={t} className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
            {t}
          </li>
        ))}
      </ul>
    </div>
    <p className="relative text-xs text-emerald-100/60">Hotshot · Delivery · Contractors · Junk Removal · Mobile Mechanics</p>
  </div>
);

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isLogin) await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      navigate("/dashboard");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <BrandPanel />
      <div className="flex flex-col justify-center px-6 sm:px-12 py-12">
        <div className="w-full max-w-md mx-auto mt-fade-up">
          <div className="lg:hidden mb-10">
            <Logo />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            {isLogin ? "Welcome back" : "Create your account"}
          </h1>
          <p className="text-slate-500 mt-2">{isLogin ? "Log in to see your miles and expenses." : "Create your free account. Mileage tracking, expense tracking, and tax reports are included with MileageTax Monthly for $9.99/month."}</p>
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid={`${mode}-form`}>
            {!isLogin && (
              <Field label="Your name" htmlFor="name">
                <Input id="name" autoComplete="name" value={form.name} onChange={set("name")} className="h-12 text-base" data-testid="register-name-input" />
              </Field>
            )}
            <Field label="Email" htmlFor="email">
              <Input id="email" type="email" autoComplete="email" required value={form.email} onChange={set("email")} className="h-12 text-base" data-testid={`${mode}-email-input`} />
            </Field>
            <Field label="Password" htmlFor="password" hint={!isLogin ? "At least 6 characters" : undefined}>
              <Input id="password" type="password" autoComplete={isLogin ? "current-password" : "new-password"} required minLength={isLogin ? undefined : 6} value={form.password} onChange={set("password")} className="h-12 text-base" data-testid={`${mode}-password-input`} />
            </Field>
            {isLogin && (
              <div className="flex justify-end -mt-2">
                <Link to="/forgot-password" className="text-sm font-semibold text-emerald-700 hover:text-emerald-900" data-testid="login-forgot-password-link">
                  Forgot password?
                </Link>
              </div>
            )}
            {error && (
              <p className="text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5" data-testid={`${mode}-error`}>
                {error}
              </p>
            )}
            <button type="submit" disabled={loading} className="mt-btn-primary w-full" data-testid={`${mode}-submit-button`}>
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              {isLogin ? "Log In" : "Create Account"}
            </button>
            {!isLogin && (
              <p className="text-xs sm:text-sm text-slate-500 text-center leading-relaxed" data-testid="register-legal-notice">
                By creating an account, you agree to our{" "}
                <Link to="/terms" className="font-semibold text-slate-700 underline underline-offset-2 hover:text-emerald-700" data-testid="register-terms-link">Terms of Service</Link>
                {" "}and{" "}
                <Link to="/privacy" className="font-semibold text-slate-700 underline underline-offset-2 hover:text-emerald-700" data-testid="register-privacy-link">Privacy Policy</Link>.
              </p>
            )}
          </form>
          <p className="mt-8 text-center text-slate-600">
            {isLogin ? "New to MileageTax? " : "Already have an account? "}
            <Link to={isLogin ? "/register" : "/login"} className="font-bold text-emerald-700 hover:text-emerald-900" data-testid={`${mode}-switch-link`}>
              {isLogin ? "Create an account" : "Log in"}
            </Link>
          </p>
          <LegalFooter className="mt-12" />
        </div>
      </div>
    </div>
  );
}
